/**
 * Vue 3 Composition API 방식 번역 파일 로더
 * - 모듈별 번역 파일 (modules/모듈명/언어.json)
 * - Vue i18n legacy 모드 문제 해결
 * - 하이브리드 로딩: 로컬 개발(정적) + 서버(동적)
 */

import { LocaleLoader } from './dynamicLoader'; // 동적 로더 import
import { ref, watch, isRef, unref } from 'vue';

/**
 * 언어 메타데이터 타입 정의
 */
export interface LanguageMetadata {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  countryCode: string; // 소문자 국가 코드 (예: 'kr', 'cn', 'sg')
}

// 환경 감지 (development, localdev 모드 모두 정적 로딩 사용)
const isLocalDev = import.meta.env.DEV && (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'localdev');

let moduleTranslations: Record<string, any> = {};
let localeLoader: LocaleLoader | null = null;

// 서버 환경에서 사용할 전역 번역 캐시 (모든 createSyncT 인스턴스가 공유)
const globalTranslationCache: Record<string, Record<string, any>> = {};

// 모든 환경에서 정적 import (번들에 포함됨)
const staticModuleTranslations = import.meta.glob('./modules/*/*.json', { eager: true });
console.log('📦 Static module translations loaded:', Object.keys(staticModuleTranslations));

if (isLocalDev) {
  // 로컬 개발: 정적 import
  moduleTranslations = staticModuleTranslations;
  console.log('📦 Local Dev: Using static module translations');
} else {
  // 개발서버 & 운영: 동적 로더 (언어 변경용)
  localeLoader = new LocaleLoader();
  console.log('🏗️ Dev/Prod Server: Initializing dynamic locale loader for language changes.');
}

/**
 * 번역 파일에서 언어 코드 추출
 * 예: './en.json' -> 'en', './modules/menu/zh-tw.json' -> 'zh-tw'
 */
const getLocaleFromPath = (path: string): string | null => {
  // zh-tw, zh-cn 같은 복합 언어 코드도 지원
  const match = path.match(/([a-z]{2,3}(?:-[a-z]{2})?)\.json$/i);
  return match ? match[1].toLowerCase() : null;
};

/**
 * 번역 파일 경로에서 모듈 이름 추출
 * 예: './modules/auth/ko.json' -> 'auth', './modules/signUp/en.json' -> 'signUp'
 */
const getModuleNameFromPath = (path: string): string | null => {
  // modules 폴더 다음의 디렉토리 이름이 모듈 이름
  const match = path.match(/modules\/([^/]+)\//);
  return match ? match[1] : null;
};

/**
 * 파일 시스템에서 모듈 목록 자동 감지
 * 로컬 개발: import.meta.glob 사용 (빌드 타임)
 * 서버 환경: 빌드 타임에 생성된 module-list.json 사용 (런타임)
 */
export const detectModules = (): string[] => {
  const modules = new Set<string>();
  
  if (isLocalDev && moduleTranslations) {
    // 로컬 개발: 정적 파일에서 모듈 이름 추출 (빌드 타임)
    Object.keys(moduleTranslations).forEach(path => {
      const moduleName = getModuleNameFromPath(path);
      if (moduleName) {
        modules.add(moduleName);
      }
    });
  } else {
    // 서버 환경(dist 빌드): 빌드 타임에 생성된 module-list.json 사용
    // vite.config.ts에서 빌드 시 public/locales/modules를 스캔하여 module-list.json 생성
    try {
      // 빌드 타임에 import.meta.glob으로 module-list.json을 포함시킴
      // (vite.config.ts에서 generateModuleList()가 생성)
      const moduleListFiles = import.meta.glob('./module-list.json', { eager: true });
      const moduleListData = Object.values(moduleListFiles)[0] as any;
      
      if (moduleListData && (moduleListData.default || Array.isArray(moduleListData))) {
        const moduleList = moduleListData.default || moduleListData;
        if (Array.isArray(moduleList)) {
          moduleList.forEach((module: string) => {
            modules.add(module);
          });
          console.log('📁 Loaded modules from build-time list:', Array.from(modules));
        }
      }
    } catch (error) {
      // module-list.json이 없으면 (개발 모드 등) import.meta.glob으로 src/modules 시도
      try {
        const serverModules = import.meta.glob('./modules/*/*.json', { eager: true });
        Object.keys(serverModules).forEach(path => {
          const moduleName = getModuleNameFromPath(path);
          if (moduleName) {
            modules.add(moduleName);
          }
        });
      } catch (globError) {
        console.debug('Failed to detect modules from glob:', globError);
      }
      
      if (modules.size === 0) {
        console.warn('⚠️ Failed to detect modules, will use fallback list');
      }
    }
  }
  
  const moduleList = Array.from(modules).sort();
  if (moduleList.length > 0) {
    console.log('📁 Detected translation modules:', moduleList);
  }
  return moduleList;
};

/**
 * 언어 메타데이터 (표시 이름, 국가 등)
 * 초기값: 하드코딩된 기본값 (L_LANGUAGES 테이블에서 조회 후 업데이트됨)
 * detectSupportedLocales보다 먼저 선언되어야 함
 */
let languageMetadata: Record<string, LanguageMetadata> = {
  en: { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', countryCode: 'us' }, // 미국 영어 (회원 가입 화면에서는 싱가폴로 표시)
  'en-GB': { code: 'en-GB', name: 'English (UK)', nativeName: 'English (UK)', flag: '🇬🇧', countryCode: 'gb' }, // 영국 영어
  ko: { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', countryCode: 'kr' }, // DB: COUNTRY_CODE = KR
  vn: { code: 'vn', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', countryCode: 'vn' }, // DB: COUNTRY_CODE = VN
  ms: { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾', countryCode: 'my' }, // DB: COUNTRY_CODE = MY
  'zh-cn': { code: 'zh-cn', name: 'Chinese (Simplified)', nativeName: '简体中文', flag: '🇨🇳', countryCode: 'cn' }, // DB: COUNTRY_CODE = CN
  'zh-tw': { code: 'zh-tw', name: 'Chinese (Traditional)', nativeName: '繁體中文', flag: '🇹🇼', countryCode: 'tw' }, // DB: COUNTRY_CODE = TW
};


/**
 * 기본 언어 코드 (fallback용)
 */
const DEFAULT_LANGUAGE_CODE = 'en';

/**
 * 전역 언어 변경 트리거
 * - 많은 컴포넌트가 localStorage 기반으로 언어를 읽고 있어, 값 변경만으로는 Vue re-render가 트리거되지 않음
 * - language-changed / translations-loaded 이벤트를 reactive tick으로 연결해 즉시 UI 갱신을 보장
 */
const languageChangeTick = ref(0);
if (typeof window !== 'undefined') {
  window.addEventListener('language-changed', () => {
    languageChangeTick.value++;
  });
  window.addEventListener('translations-loaded', () => {
    languageChangeTick.value++;
  });
}

/**
 * 기본 국가 이름 (fallback용)
 * 순환 참조 방지를 위해 languageMetadata를 직접 사용
 */
const getDefaultCountryName = (): string => {
  const metadataKeys = Object.keys(languageMetadata);
  if (metadataKeys.length > 0) {
    const firstLangCode = metadataKeys[0];
    const countryName = getCountryNameFromLanguageCodeInternal(firstLangCode);
    if (countryName) {
      return countryName;
    }
  }
  // 최종 fallback
  return 'Singapore';
};

/**
 * 언어 메타데이터와 displayOrder를 저장
 */
let languageDisplayOrder: Record<string, number> = {};

/**
 * 파일 시스템에서 지원 언어 자동 감지 (Vue 3 방식)
 */
const detectSupportedLocales = (): string[] => {
  const locales = new Set<string>();
  
  if (isLocalDev) {
    // 로컬 개발: 정적 파일에서 언어 추출
    Object.keys(moduleTranslations).forEach(path => {
      const locale = getLocaleFromPath(path);
      if (locale) {
        locales.add(locale);
      }
    });
  } else {
    // 서버: languageMetadata에서 언어 코드 추출
    // languageMetadata가 로드되지 않았으면 fallback 사용
    const metadataKeys = Object.keys(languageMetadata);
    if (metadataKeys.length > 0) {
      // languageMetadata에서 언어 코드 추출 (DISPLAY_ORDER 정렬)
      return metadataKeys.sort((a, b) => {
        const orderA = languageDisplayOrder[a] ?? 999;
        const orderB = languageDisplayOrder[b] ?? 999;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return a.localeCompare(b);
      });
    }
    // languageMetadata가 비어있으면 빈 배열 반환 (파일 시스템에서 감지된 locales 사용)
    return [];
  }

  const localeList = Array.from(locales).sort();
  console.log('🌐 Detected supported locales:', localeList);
  return localeList;
};

const supportedLocales = detectSupportedLocales();

/**
 * 언어별 번역 데이터 구성 (하이브리드 방식)
 */
const buildMessages = (): Record<string, any> => {
  const messages: Record<string, any> = {};

  // ✅ 모든 환경에서 정적 번들(import.meta.glob eager)로 초기 메시지를 "동기적으로" 시드한다.
  //   - 이전에는 서버 빌드(isLocalDev=false)에서 빈 메시지 `{}`로 시작하고 mount 후 비동기 로드에 의존했음.
  //   - 그 사이 vue-i18n을 직접 쓰는 화면(Register MY 등)이 빈 메시지/다른 언어로 렌더되어
  //     "영어 UI인데 일부만 한글" 레이스가 간헐적으로 발생 → 정적 시드로 창(window) 자체를 제거.
  //   - 동적 로더는 언어 변경 시 여전히 override 하므로 기능 회귀 없음.
  Object.entries(staticModuleTranslations).forEach(([path, module]) => {
    const locale = getLocaleFromPath(path);
    if (locale) {
      const translationData = (module as any).default || module;
      if (translationData) {
        if (!messages[locale]) messages[locale] = {};
        messages[locale] = { ...messages[locale], ...translationData };
      }
    }
  });

  if (isLocalDev) {
    console.log('📦 Local Dev: Using static module translations');
  } else {
    console.log('🏗️ Dev/Prod Server: Seeded initial messages from static bundle (dynamic loader still handles language changes).');
  }

  return messages;
};

const messages = buildMessages();

// ✅ createSyncT 가 쓰는 전역 캐시도 정적 번들로 "동기" 시드한다.
//   - 이전: 캐시는 main.ts initializeTranslations()(= L_LANGUAGES API await 이후)에서만 채워져
//     app.mount 직후 첫 렌더는 빈 캐시 → t()가 키("auth.welcomeToLettaga")를 그대로 반환했고,
//     그 사이 t() 호출마다 모듈 전체(~28개) fetch 가 중복 발사되어 nginx 429 / ERR_INSUFFICIENT_RESOURCES 유발.
//   - 지금: import 시점에 번들에서 바로 채우므로 첫 렌더부터 번역이 있고, 백그라운드 fetch 가 필요 없다.
Object.entries(staticModuleTranslations).forEach(([path, module]) => {
  const locale = getLocaleFromPath(path);
  const moduleName = getModuleNameFromPath(path);
  if (!locale || !moduleName) return;
  const translationData = (module as any).default || module;
  if (!translationData) return;
  if (!globalTranslationCache[locale]) globalTranslationCache[locale] = {};
  globalTranslationCache[locale][moduleName] = translationData;
});
if (globalTranslationCache['en'] && !globalTranslationCache['en-GB']) {
  globalTranslationCache['en-GB'] = { ...globalTranslationCache['en'] };
}
console.log('📦 Seeded sync translation cache from static bundle:', Object.keys(globalTranslationCache));

// 언어별 백그라운드 로드 in-flight 중복 방지 (createSyncT 수천 회 호출 → fetch 1회)
const backgroundLoadInFlight = new Map<string, Promise<void>>();

// 번역 파일은 en.json 등만 있고 en-GB.json은 없음. 영어 브라우저 기본 locale이 en-GB이므로
// messages['en-GB']가 비면 vue-i18n이 빈 메시지로 렌더 → 다른 언어로 보이는 문제 발생.
// ✅ 모든 환경에서 en 내용을 en-GB로 복제해 항상 영어 메시지를 보유하도록 한다.
if (messages['en']) {
  const cloneDeep = (obj: Record<string, unknown>) => {
    try {
      return structuredClone(obj) as Record<string, any>;
    } catch {
      return JSON.parse(JSON.stringify(obj)) as Record<string, any>;
    }
  };
  if (!messages['en-GB'] || Object.keys(messages['en-GB']).length === 0) {
    messages['en-GB'] = cloneDeep(messages['en'] as Record<string, unknown>);
  }
}

console.log('🎉 Final messages structure:', Object.keys(messages));
console.log('📖 Full messages data:', messages);

/**
 * 언어 메타데이터를 L_LANGUAGES 테이블에서 조회한 값으로 업데이트
 */
export const loadLanguageMetadata = async (): Promise<void> => {
  try {
    // baseCodeApi를 동적으로 import하여 순환 참조 방지
    const { baseCodeApi } = await import('@/shared/api/baseCodeApi');
    
    const response = await baseCodeApi.getLanguages();
    
    if (response.data && response.data.languages) {
      // L_LANGUAGES 테이블의 데이터 구조로 languageMetadata 업데이트
      const newMetadata: Record<string, LanguageMetadata> = {};
      const newDisplayOrder: Record<string, number> = {};
      
      response.data.languages.forEach((lang) => {
        let countryCode = lang.countryCode?.toLowerCase() || 'xx';
        
        // 영어 언어 코드별 국기 매핑
        if (lang.code === 'en-GB') {
          countryCode = 'gb';  // 영국 영어는 영국 국기
        } else if (lang.code === 'en') {
          // 기본 영어는 미국 국기
          // 하지만 회원 가입 화면에서는 싱가폴로 표시됨 (getCountryNameFromLanguageCodeInternal에서 처리)
          countryCode = 'us';  // 미국 영어
        }
        
        newMetadata[lang.code] = {
          code: lang.code,
          name: lang.name,
          nativeName: lang.nativeName,
          flag: lang.flag || '🌐', // FLAG_ICON이 없으면 기본값 사용
          countryCode: countryCode // 소문자 국가 코드
        };
        newDisplayOrder[lang.code] = lang.displayOrder || 999; // DISPLAY_ORDER 저장
      });
      
      languageMetadata = newMetadata;
      languageDisplayOrder = newDisplayOrder;
      
      console.log('✅ Language metadata loaded from L_LANGUAGES table:', Object.keys(languageMetadata));
    }
  } catch (error) {
    console.warn('⚠️ Failed to load language metadata from API, using default values:', error);
    // API 호출 실패 시 기본값 유지
  }
};

/**
 * 언어 메타데이터 가져오기 (반응형)
 */
export const getLanguageMetadata = (): Record<string, LanguageMetadata> => {
  return languageMetadata;
};

/**
 * 언어 코드로 국가 코드 가져오기
 * @param {string} langCode - 언어 코드 (예: 'ko', 'en', 'zh-cn')
 * @returns {string} 국가 코드 (예: 'kr', 'gb', 'cn')
 */
export const getCountryCodeFromLanguage = (langCode: string): string => {
  if (!langCode) {
    return 'xx';
  }

  // 언어 코드 정규화 (en-GB 같은 경우 대소문자 유지)
  const normalizedLang = langCode.trim();
  const lowerLang = normalizedLang.toLowerCase();
  
  // 먼저 정확한 키로 찾기 (en-GB 같은 경우)
  let metadata = languageMetadata[normalizedLang] || languageMetadata[lowerLang];
  
  if (metadata && metadata.countryCode) {
    return metadata.countryCode.toLowerCase();
  }

  // Fallback: 언어 코드를 그대로 반환
  return lowerLang;
};

/**
 * 언어 코드로 국기 이미지 URL 가져오기
 * @param {string} langCode - 언어 코드 (예: 'ko', 'en', 'zh-cn')
 * @param {number} size - 국기 이미지 크기 (기본값: 20)
 * @returns {string} 국기 이미지 URL
 */
export const getLanguageFlagUrl = (langCode: string, size: number = 20): string => {
  if (!langCode) {
    return `https://flagcdn.com/w${size}/xx.png`;
  }

  // 언어 코드 정규화 (en-GB 같은 경우 대소문자 유지)
  const normalizedLang = langCode.trim();
  const lowerLang = normalizedLang.toLowerCase();
  
  // 먼저 정확한 키로 찾기 (en-GB 같은 경우)
  let metadata = languageMetadata[normalizedLang] || languageMetadata[lowerLang];
  
  // countryCode를 사용하여 flagcdn URL 생성
  if (metadata && metadata.countryCode) {
    const countryCode = metadata.countryCode.toLowerCase();
    return `https://flagcdn.com/w${size}/${countryCode}.png`;
  }

  // Fallback: getCountryCodeFromLanguage 사용
  const countryCode = getCountryCodeFromLanguage(langCode);
  return `https://flagcdn.com/w${size}/${countryCode}.png`;
};

/**
 * 국가 코드 타입 (주소 입력용)
 * 하드코딩된 국가 코드 목록
 */
export type CountryCode = 'KR' | 'CN' | 'SG' | 'HK' | 'MY' | 'VN' | 'GB' | 'US' | 'TW';

/**
 * 언어 코드로 국가 이름 가져오기 (내부 헬퍼 함수)
 * languageMetadata를 기반으로 동적으로 매핑 생성
 */
const getCountryNameFromLanguageCodeInternal = (languageCode: string): string | null => {
  const lang = languageCode.toLowerCase().trim();
  const metadata = languageMetadata[lang];
  
  if (metadata) {
    // 영어(en)는 회원 가입 화면에서 싱가폴로 표시되어야 함
    if (lang === 'en') {
      return 'Singapore';
    }
    
    // countryCode를 기반으로 국가 이름 매핑
    // countryCode: 'kr' -> 'Korea', 'cn' -> 'China', 'sg' -> 'Singapore' 등
    const countryCodeToNameMap: Record<string, string> = {
      'kr': 'Korea',
      'cn': 'China',
      'sg': 'Singapore',
      'hk': 'Hong Kong',
      'my': 'Malaysia',
      'vn': 'Vietnam',
      'tw': 'Hong Kong',  // Taiwan은 Hong Kong으로 표시
      'us': 'United States',
      'gb': 'United Kingdom'
    };
    
    const countryCode = metadata.countryCode?.toLowerCase();
    if (countryCode && countryCodeToNameMap[countryCode]) {
      return countryCodeToNameMap[countryCode];
    }
  }
  
  return null;
};

/**
 * 국가 이름으로 언어 코드 가져오기 (내부 헬퍼 함수)
 * getSupportedLanguages()를 기반으로 역매핑
 */
const getLanguageCodeFromCountryNameInternal = (countryName: string): string | null => {
  const supportedLanguages = getSupportedLanguages();
  const countryToLangMap: Record<string, string> = {};
  
  // 지원 언어를 기반으로 역매핑 생성
  supportedLanguages.forEach(lang => {
    const countryNameForLang = getCountryNameFromLanguageCodeInternal(lang.code);
    if (countryNameForLang) {
      countryToLangMap[countryNameForLang] = lang.code;
    }
  });
  
  return countryToLangMap[countryName] || null;
};

/**
 * 언어 코드로 국가 목록 가져오기 (내부 헬퍼 함수)
 */
const getCountriesByLanguageCodeInternal = (languageCode: string): string[] => {
  const countryName = getCountryNameFromLanguageCodeInternal(languageCode);
  return countryName ? [countryName] : [];
};

/**
 * 국가 이름으로 국기 코드 가져오기 (내부 헬퍼 함수)
 * 국가 이름에 따라 직접 국기 코드를 매핑 (언어 코드 기반이 아님)
 */
const getFlagCodeFromCountryNameInternal = (countryName: string): string => {
  if (!countryName) {
    return 'xx';
  }
  
  // 국가 이름에 따른 직접 매핑 (회원 가입 폼에서 사용)
  const countryToFlagCodeMap: Record<string, string> = {
    'Singapore': 'sg',      // 싱가폴은 싱가폴 국기
    'Taiwan': 'hk',          // Taiwan은 홍콩 국기 (레거시 지원, 실제로는 'Hong Kong'으로 표시됨)
    'Hong Kong': 'hk',       // 홍콩은 홍콩 국기
    'Korea': 'kr',           // 한국은 한국 국기
    'China': 'cn',            // 중국은 중국 국기
    'Malaysia': 'my',         // 말레이시아는 말레이시아 국기
    'Vietnam': 'vn',          // 베트남은 베트남 국기
  };
  
  // 직접 매핑이 있으면 사용
  const directMapping = countryToFlagCodeMap[countryName];
  if (directMapping) {
    return directMapping;
  }
  
  // 직접 매핑이 없으면 언어 코드 기반으로 찾기 (fallback)
  const languageCode = getLanguageCodeFromCountryNameInternal(countryName);
  if (!languageCode) {
    return 'xx';
  }
  
  const supportedLanguages = getSupportedLanguages();
  const lang = supportedLanguages.find(l => l.code === languageCode);
  
  if (lang && lang.countryCode && lang.countryCode !== 'xx') {
    return lang.countryCode.toLowerCase();
  }
  
  return 'xx';
};

/**
 * 국가 이름으로 언어 코드 가져오기
 * @param {string} countryName - 국가 이름 (예: 'Singapore', 'Malaysia', 'Vietnam')
 * @returns {string} 언어 코드 (예: 'en', 'ms', 'vn')
 */
export const getLanguageCodeFromCountryName = (countryName: string): string => {
  if (!countryName) {
    // 기본값: getSupportedLanguages()의 첫 번째 언어 또는 DEFAULT_LANGUAGE_CODE
    const supportedLanguages = getSupportedLanguages();
    return supportedLanguages.length > 0 ? supportedLanguages[0].code : DEFAULT_LANGUAGE_CODE;
  }
  const langCode = getLanguageCodeFromCountryNameInternal(countryName);
  return langCode || DEFAULT_LANGUAGE_CODE;
};

/**
 * 언어 코드로 국가 목록 가져오기
 * @param {string} languageCode - 언어 코드 (예: 'en', 'ko', 'zh-cn')
 * @returns {string[]} 국가 이름 배열 (예: ['Singapore'])
 */
export const getCountriesByLanguageCode = (languageCode: string): string[] => {
  if (!languageCode) {
    return [];
  }
  return getCountriesByLanguageCodeInternal(languageCode.toLowerCase());
};

/**
 * 국가 이름으로 국기 코드 가져오기
 * @param {string} countryName - 국가 이름 (예: 'Singapore', 'Korea')
 * @returns {string} 국기 코드 (예: 'sg', 'kr')
 */
export const getFlagCodeFromCountryName = (countryName: string): string => {
  if (!countryName) {
    return 'xx';
  }
  return getFlagCodeFromCountryNameInternal(countryName);
};

/**
 * 국가 이름으로 국기 이미지 URL 가져오기
 * @param {string} countryName - 국가 이름 (예: 'Singapore', 'Korea')
 * @param {number} size - 국기 이미지 크기 (기본값: 40)
 * @returns {string} 국기 이미지 URL
 */
export const getCountryFlagUrl = (countryName: string, size: number = 40): string => {
  const flagCode = getFlagCodeFromCountryName(countryName);
  if (flagCode && flagCode !== 'xx') {
    return `https://flagcdn.com/w${size}/${flagCode}.png`;
  }
  
  // Fallback: 언어 코드를 통해 국기 가져오기
  const languageCode = getLanguageCodeFromCountryName(countryName);
  return getLanguageFlagUrl(languageCode, size);
};

/**
 * 언어 코드로 기본 국가 가져오기
 * 같은 언어를 사용하는 국가가 여러 개인 경우, 첫 번째 국가를 반환
 * @param {string} languageCode - 언어 코드 (예: 'en', 'ko')
 * @returns {string} 국가 이름 (예: 'Singapore', 'Korea')
 */
export const getDefaultCountryByLanguage = (languageCode: string): string => {
  if (!languageCode) {
    return getDefaultCountryName();
  }
  const countries = getCountriesByLanguageCode(languageCode);
  return countries.length > 0 ? countries[0] : getDefaultCountryName();
};

/**
 * 지원되는 모든 국가 목록 가져오기
 * @returns {Array<{label: string, value: string, flag: string, languageCode: string}>} 국가 목록
 */
export const getSupportedCountries = (): Array<{label: string, value: string, flag: string, languageCode: string}> => {
  const supportedLanguages = getSupportedLanguages();
  const countrySet = new Set<string>();
  
  // 지원되는 모든 언어 코드에 대해 해당 국가들을 추가
  supportedLanguages.forEach(lang => {
    const countryName = getCountryNameFromLanguageCodeInternal(lang.code);
    if (countryName) {
      countrySet.add(countryName);
    }
  });
  
  // 국가 목록을 생성 (국기 코드와 언어 코드 포함)
  return Array.from(countrySet)
    .map((country: string) => ({
      label: country,
      value: country,
      flag: getFlagCodeFromCountryName(country),
      languageCode: getLanguageCodeFromCountryName(country)
    }))
    .sort((a, b) => a.label.localeCompare(b.label)); // 알파벳 순 정렬
};

/**
 * 지원 언어 목록 (메타데이터 포함)
 */
export const getSupportedLanguages = (): LanguageMetadata[] => {
  // languageMetadata에서 활성화된 언어만 반환
  const metadataKeys = Object.keys(languageMetadata);
  
  // languageMetadata가 비어있으면 supportedLocales 사용 (파일 시스템에서 감지)
  // languageMetadata가 있으면 그것을 우선 사용
  const activeLocales = metadataKeys.length > 0 ? metadataKeys : supportedLocales;
  
  return activeLocales
    .map(locale => languageMetadata[locale] || {
      code: locale,
      name: locale.toUpperCase(),
      nativeName: locale.toUpperCase(),
      flag: '🌐',
      countryCode: 'xx'
    })
    .filter(Boolean)
    .sort((a, b) => {
      // ABC 순(알파벳 순)으로 정렬 (영어 이름 기준)
      return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
    });
};

/**
 * 지원 언어 코드 목록만 가져오기 (순환 참조 방지)
 */
export const getSupportedLanguageCodes = (): string[] => {
  // languageMetadata가 있으면 그것을 우선 사용
  const metadataKeys = Object.keys(languageMetadata);
  if (metadataKeys.length > 0) {
    // DISPLAY_ORDER에 따라 정렬
    return metadataKeys.sort((a, b) => {
      const orderA = languageDisplayOrder[a] ?? 999;
      const orderB = languageDisplayOrder[b] ?? 999;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.localeCompare(b);
    });
  }
  
  // languageMetadata가 없으면 supportedLocales 사용
  return supportedLocales;
};

/**
 * 언어 코드를 Vuetify locale 형식으로 변환
 * @param {string} langCode - 언어 코드 (예: 'ko', 'en', 'zh-cn')
 * @returns {string} Vuetify locale 형식 (예: 'ko-KR', 'en-US', 'zh-CN')
 */
export const getVuetifyLocale = (langCode: string): string => {
  if (!langCode) {
    return 'en-US';
  }

  const lang = langCode.toLowerCase().trim();
  
  // 언어 코드 -> Vuetify locale 매핑
  const languageToVuetifyLocale: Record<string, string> = {
    'ko': 'ko-KR',
    'en': 'en-US',        // 기본 영어는 en-US
    'en-gb': 'en-GB',     // 영국 영어
    'zh-cn': 'zh-CN',
    'zh-tw': 'zh-TW',
    'vn': 'vi-VN',
    'ms': 'ms-MY',
    'sg': 'en-SG',
  };
  
  return languageToVuetifyLocale[lang] || 'en-US';
};

/**
 * 중첩된 객체에서 점(.) 표기법으로 값 가져오기
 * 예: getNestedValue({ auth: { Login: '로그인' } }, 'auth.Login') -> '로그인'
 *
 * 두 가지 패턴 지원:
 * 1. 중첩 구조: 'register.companySelect.label' -> register["companySelect"]["label"]
 * 2. 플랫 구조: 'startTask.Please select.' -> startTask["Please select."]
 *
 * 먼저 중첩 구조로 시도하고, 실패하면 플랫 구조로 시도
 */
const getNestedValue = (obj: any, path: string): string | undefined => {
  if (!obj || !path) {
    return undefined;
  }

  // 1. 먼저 중첩 구조로 시도 (모든 점을 구분자로 사용)
  const nestedKeys = path.split('.');
  let current = obj;

  for (const key of nestedKeys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      current = undefined;
      break;
    }
  }

  if (typeof current === 'string') {
    return current;
  }

  // 2. 중첩 구조에서 찾지 못하면 플랫 구조로 시도 (첫 번째 점만 구분자로 사용)
  const firstDotIndex = path.indexOf('.');
  if (firstDotIndex !== -1) {
    const moduleName = path.substring(0, firstDotIndex);
    const keyName = path.substring(firstDotIndex + 1);

    if (obj && typeof obj === 'object' && moduleName in obj) {
      const moduleObj = obj[moduleName];
      if (moduleObj && typeof moduleObj === 'object' && keyName in moduleObj) {
        const value = moduleObj[keyName];
        if (typeof value === 'string') {
          return value;
        }
      }
    }
  }

  return undefined;
};

/**
 * Vue 3 Composition API 방식 번역 함수 (하이브리드)
 */
export const useTranslation = () => {
  const getCurrentLanguage = (): string => {
    return localStorage.getItem('preferred-language') || DEFAULT_LANGUAGE_CODE;
  };

  // 반응형 번역 함수 생성 (하이브리드)
  const createReactiveT = (currentLanguage: any) => {
    return async (key: string): Promise<string> => { // async로 변경
      // ✅ language change tick dependency (forces reactive re-render on language change)
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      languageChangeTick.value;

      const currentLang = currentLanguage.value || getCurrentLanguage();
      
      if (!isLocalDev && localeLoader) {
        // 서버: 동적 로딩
        try {
          // 모듈 목록 자동 감지
          const modules = detectModules();
          let dynamicMessages: Record<string, any> = {};
          
          for (const moduleName of modules) {
            const translations = await localeLoader.loadLocale(moduleName, currentLang);
            if (translations) {
              dynamicMessages[moduleName] = translations;
            }
          }
          
          const translation = getNestedValue(dynamicMessages, key);
          if (translation) {
            return translation;
          }
          
          // Fallback to English
          let fallbackMessages: Record<string, any> = {};
          // modules 변수가 이미 위에서 정의되어 있으므로 재사용
          for (const moduleName of modules) {
            const translations = await localeLoader.loadLocale(moduleName, DEFAULT_LANGUAGE_CODE);
            if (translations) {
              fallbackMessages[moduleName] = translations;
            }
          }
          const fallback = getNestedValue(fallbackMessages, key);
          if (fallback) {
            console.warn(`Translation missing for "${key}" in ${currentLang}, using English fallback`);
            return fallback;
          }
        } catch (error) {
          console.warn('Failed to load dynamic translations:', error);
        }
      } else if (isLocalDev) {
        // 로컬 개발: 정적 메시지 사용
        const translation = getNestedValue(messages[currentLang], key);
        if (translation) {
          return translation;
        }

        const fallback = getNestedValue(messages[DEFAULT_LANGUAGE_CODE], key);
        if (fallback) {
          console.warn(`Translation missing for "${key}" in ${currentLang}, using English fallback`);
          return fallback;
        }
      }

      // return key if no translation found
      console.warn(`Translation missing for "${key}" in all languages`);
      return key;
    };
  };

  // 동기 번역 함수 생성 (기존 코드와의 호환성을 위해)
  // options.silent: true이면 번역 누락 경고 숨김 (tl() fallback 패턴용)
  const createSyncT = (currentLanguage: any, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    // 언어 변경 감지를 위한 플래그 (반응형 업데이트용)
    let languageChangeCount = 0;
    
    // 언어 변경 감지 (watch를 사용하여 반응형으로 만들기)
    if (typeof currentLanguage === 'object' && currentLanguage.value !== undefined) {
      watch(() => currentLanguage.value, (newLang, oldLang) => {
        if (newLang && newLang !== oldLang && !isLocalDev && localeLoader) {
          // 언어가 변경되면 번역 파일 로드
          languageChangeCount++;
          console.log(`🌐 Language changed detected in createSyncT: ${oldLang} -> ${newLang}`);
          
          // 비동기로 번역 파일 로드 (자동 감지된 모듈 사용)
          const modules = detectModules();
          const modulesToLoad = modules.length > 0 ? modules : [];
          if (modulesToLoad.length === 0) {
            console.warn('⚠️ No modules detected, skipping translation load');
            return;
          }
          localeLoader.loadMultipleModules(modulesToLoad, newLang).then(translations => {
            if (translations) {
              Object.entries(translations).forEach(([module, data]) => {
                if (data) {
                  updateGlobalTranslationCache(newLang, module, data);
                }
              });
              console.log(`✅ Translations loaded for language change: ${newLang}`);
              
              // 언어 변경 이벤트 발생 (UI 업데이트 트리거)
              window.dispatchEvent(new CustomEvent('translations-loaded', {
                detail: { language: newLang }
              }));
            }
          }).catch(err => {
            console.warn(`⚠️ Failed to load translations for ${newLang}:`, err);
          });
        }
      });
    }
    
    return (key: string): string => {
      // ✅ language change tick dependency (forces reactive re-render on language change)
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      languageChangeTick.value;

      const currentLang = (isRef(currentLanguage) ? unref(currentLanguage) : currentLanguage) || getCurrentLanguage();
      
      if (isLocalDev) {
        // 로컬 개발: 정적 메시지 사용
        const translation = getNestedValue(messages[currentLang], key);
        if (translation) {
          return translation;
        }

        const fallback = getNestedValue(messages[DEFAULT_LANGUAGE_CODE], key);
        if (fallback) {
          if (!silent) console.warn(`Translation missing for "${key}" in ${currentLang}, using English fallback`);
          return fallback;
        }
      } else {
        // 서버: 전역 캐시에서 번역 사용 (모든 모듈 병합)
        if (globalTranslationCache[currentLang]) {
          // 모든 모듈을 하나의 객체로 병합
          const mergedMessages = Object.values(globalTranslationCache[currentLang]).reduce((acc, module) => {
            return { ...acc, ...module };
          }, {});
          
          const translation = getNestedValue(mergedMessages, key);
          if (translation && translation !== key) {
            return translation;
          }
        }
        
        // 캐시에 없으면 비동기로 로드 시도 (언어당 정확히 1회 — in-flight 중복 방지)
        if (localeLoader && !globalTranslationCache[currentLang] && !backgroundLoadInFlight.has(currentLang)) {
          const modules = detectModules();
          if (modules.length === 0) {
            console.warn('⚠️ No modules detected, skipping background translation load');
          } else {
            const loader = localeLoader;
            const job = loader.loadMultipleModules(modules, currentLang).then(translations => {
              const loaded = Object.entries(translations || {}).filter(([, data]) => !!data);
              loaded.forEach(([module, data]) => updateGlobalTranslationCache(currentLang, module, data));
              if (loaded.length > 0) {
                console.log(`✅ Background loaded translations for: ${currentLang} (${loaded.length} modules)`);
                // 로드 완료 후 화면을 다시 그리도록 신호 (languageChangeTick++ 트리거) — 실제로 받은 게 있을 때만
                window.dispatchEvent(new CustomEvent('translations-loaded', { detail: { language: currentLang } }));
              } else {
                console.warn(`⚠️ No translation files found for ${currentLang}; falling back to ${DEFAULT_LANGUAGE_CODE}`);
              }
            }).catch(err => {
              console.warn(`⚠️ Failed to background load translations for ${currentLang}:`, err);
            }).finally(() => {
              // 결과와 무관하게 "시도함"을 기록 → 파일이 없는 언어에서 t() 호출마다 28건 재요청이 반복되지 않음.
              // (빈 객체면 위의 기본 언어 fallback 으로 떨어지고, 언어 변경 리스너(main.ts)가 다시 채울 수 있음)
              if (!globalTranslationCache[currentLang]) globalTranslationCache[currentLang] = {};
              backgroundLoadInFlight.delete(currentLang);
            });
            backgroundLoadInFlight.set(currentLang, job);
          }
        }
        
        // 기본 언어 fallback
        if (globalTranslationCache[DEFAULT_LANGUAGE_CODE]) {
          const mergedMessages = Object.values(globalTranslationCache[DEFAULT_LANGUAGE_CODE]).reduce((acc, module) => {
            return { ...acc, ...module };
          }, {});
          
          const fallback = getNestedValue(mergedMessages, key);
          if (fallback && fallback !== key) {
            return fallback;
          }
        }
        
        // 캐시에 없으면 키 반환
        return key;
      }

      // return key if no translation found
      if (!silent) console.warn(`Translation missing for "${key}" in all languages`);
      return key;
    };
  };

  const setLanguage = (langCode: string): void => {
    localStorage.setItem('preferred-language', langCode);
    console.log(`Language changed to: ${langCode}`);
    // 언어 변경 이벤트 발생
    window.dispatchEvent(new CustomEvent('language-changed', {
      detail: { language: langCode }
    }));
  };

  return {
    createReactiveT,
    createSyncT, // 동기 버전 추가
    setLanguage,
    getCurrentLanguage,
    supportedLanguages: getSupportedLanguages()
  };
};


/**
 * 전역 번역 캐시에 번역 데이터 추가 (서버 환경용)
 * @param locale - 언어 코드
 * @param moduleName - 모듈 이름
 * @param translations - 번역 데이터
 */
export const updateGlobalTranslationCache = (locale: string, moduleName: string, translations: any): void => {
  if (!globalTranslationCache[locale]) {
    globalTranslationCache[locale] = {};
  }
  if (translations) {
    globalTranslationCache[locale][moduleName] = translations;
  }
};

/**
 * 전역 번역 캐시 가져오기
 */
export const getGlobalTranslationCache = (): Record<string, Record<string, any>> => {
  return globalTranslationCache;
};

/**
 * 정적으로 로드된 모듈 번역 데이터 반환 (번들에 포함됨)
 */
export const getStaticModuleTranslations = (): Record<string, any> => {
  return staticModuleTranslations;
};

export { supportedLocales };
export default messages;
